// components/Modals/BuyPackages/PaymentProviderSelector.tsx
import React from "react";
import { View, Text, TouchableOpacity, StyleSheet, Image } from "react-native";
import { useTranslation } from "react-i18next";

export type PaymentProvider = "stripe" | "paypal" | "epay";

interface PaymentProviderSelectorProps {
  selectedProvider: PaymentProvider;
  onSelectProvider: (provider: PaymentProvider) => void;
}

const PaymentProviderSelector: React.FC<PaymentProviderSelectorProps> = ({
  selectedProvider,
  onSelectProvider,
}) => {
  const { t } = useTranslation();

  const providers: { value: PaymentProvider; label: string; enabled: boolean }[] = [
    { value: "stripe", label: "Credit Card (Stripe)", enabled: true },
    { value: "paypal", label: "PayPal", enabled: true },
    { value: "epay", label: "Epay", enabled: true },
  ];

  return (
    <View style={styles.container}>
      <Text style={styles.title}>{t("T_SITE_SHOP_CHOOSE_PAYMENT_METHOD") || "Select Payment Method"}</Text>
      <View style={styles.providersContainer}>
        {providers.map((provider) => (
          <TouchableOpacity
            key={provider.value}
            style={[
              styles.providerOption,
              selectedProvider === provider.value && styles.providerOptionSelected,
              !provider.enabled && styles.providerOptionDisabled,
            ]}
            onPress={() => provider.enabled && onSelectProvider(provider.value)}
            disabled={!provider.enabled}
          >
            {/* Radio Button */}
            <View style={styles.radioContainer}>
              <View
                style={[
                  styles.radioOuter,
                  selectedProvider === provider.value && styles.radioOuterSelected,
                  !provider.enabled && styles.radioOuterDisabled,
                ]}
              >
                {selectedProvider === provider.value && (
                  <View style={styles.radioInner} />
                )}
              </View>
            </View>

            {/* Label */}
            <Text
              style={[
                styles.providerLabel,
                !provider.enabled && styles.providerLabelDisabled,
              ]}
            >
              {provider.label}
            </Text>

            {/* Coming Soon Badge */}
            {!provider.enabled && (
              <View style={styles.comingSoonBadge}>
                <Text style={styles.comingSoonText}>Coming Soon</Text>
              </View>
            )}
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: "100%",
    marginVertical: 20,
    paddingHorizontal: 20,
    alignItems: "center", // Center the entire section
  },
  title: {
    color: "#fff",
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: 15,
    textAlign: "center",
  },
  providersContainer: {
    gap: 12,
    maxWidth: 450, // Limit max width
    width: "100%", // Responsive but capped
  },
  providerOption: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    borderRadius: 12,
    padding: 14, //  Slightly reduced padding
    paddingHorizontal: 16,
    borderWidth: 2,
    borderColor: "transparent",
  },
  providerOptionSelected: {
    backgroundColor: "rgba(0, 240, 255, 0.1)",
    borderColor: "#00f0ff",
  },
  providerOptionDisabled: {
    opacity: 0.5,
  },
  radioContainer: {
    marginRight: 12,
  },
  radioOuter: {
    width: 22, // Slightly smaller
    height: 22,
    borderRadius: 11,
    borderWidth: 2,
    borderColor: "#666",
    justifyContent: "center",
    alignItems: "center",
  },
  radioOuterSelected: {
    borderColor: "#00f0ff",
  },
  radioOuterDisabled: {
    borderColor: "#444",
  },
  radioInner: {
    width: 10, // Proportionally smaller
    height: 10,
    borderRadius: 5,
    backgroundColor: "#00f0ff",
  },
  providerLabel: {
    flex: 1,
    color: "#fff",
    fontSize: 15, // Slightly smaller font
    fontWeight: "500",
  },
  providerLabelDisabled: {
    color: "#888",
  },
  comingSoonBadge: {
    backgroundColor: "rgba(255, 165, 0, 0.2)",
    paddingHorizontal: 8, // Smaller padding
    paddingVertical: 3,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "#ffa500",
  },
  comingSoonText: {
    color: "#ffa500",
    fontSize: 10, // Smaller text
    fontWeight: "600",
  },
});

export default PaymentProviderSelector;